import { useCallback, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import ModalContext from 'contexts/ModalContext'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import CruiseLuxPlusLabel from 'components/Cruises/CruiseLuxPlusLabel'
import CruisePriceFromLabel from 'components/Cruises/Common/CruisePriceFromLabel'
import CruisePromotionPriceUnit from 'components/Cruises/Common/CruisePromotionPriceUnit'
import CruisePriceSaveUpCaption from 'components/Cruises/Common/CruisePriceSaveUpCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import type { CruiseOnlyOfferView } from 'hooks/Cruise/useCruiseOfferView'

export interface CruiseOverviewPriceDetailsProps {
  view: CruiseOnlyOfferView
  hasValidDepartureDates: boolean
  priceType: 'standard' | 'bundle'
  size?: 'small' | 'large'
}

function CruiseOverviewPriceDetails({
  view,
  hasValidDepartureDates,
  priceType,
  size = 'large',
}: CruiseOverviewPriceDetailsProps) {
  const { offer, cabinCategory } = view
  const isCompact = size === 'small'
  const priceStackSize = isCompact ? 'S' : 'L'
  const lowestPrice = priceType === 'bundle' ? view.lowestBundlePrice : view.lowestPrice
  const memberLowestPrice = priceType === 'standard' ? view.memberLowestPrice : undefined
  const memberPriceType = priceType === 'standard' ? view.memberPriceType : undefined
  const discountPills = priceType === 'standard' ? view.discountPills : undefined
  const canSeeLuxPlusLabel = priceType === 'standard' && view.canSeeLuxPlusLabel
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinType = cabinCategory ? cabinTypeLabels[cabinCategory.toLowerCase()] ?? cabinCategory : undefined
  const showModal = useContext(ModalContext)
  const openDiscountTsAndCsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  if (!hasValidDepartureDates || !lowestPrice) return null

  const { primaryPrice, secondaryPrice, promotionalPrice } = lowestPrice
  const perPersonPrice = promotionalPrice?.perPersonPrice
  const hasPerPersonPrice = !!perPersonPrice

  return (
    <Group direction="vertical">
      <Group direction="vertical" gap={4}>
        {promotionalPrice && (
          <div>
            <PriceRowPriceCaption>
              <CruisePriceFromLabel promotion={promotionalPrice.promotion} cabinType={cabinType} />
            </PriceRowPriceCaption>
            {hasPerPersonPrice ? (
              <>
                <Caption variant="medium" colour="success-dark">
                  <FormattedMessage
                    id="01ZsUhS60i_gK_k4VanJ"
                    defaultMessage="{price}/person"
                    description="Primary per-person price displayed above the total family cruise promotional price"
                    values={{
                      price: (
                        <Heading as="span" variant={isCompact ? 'heading6' : 'heading4'} colour="success-dark">
                          <FormatCurrency value={perPersonPrice} />
                        </Heading>
                      ),
                    }}
                  />
                </Caption>
                <Caption variant="medium" colour="light-foreground-alt">
                  <FormatCurrency value={promotionalPrice.total} />
                  &nbsp;
                  <CruisePromotionPriceUnit promotion={promotionalPrice.promotion} />
                </Caption>
              </>
            ) : (
              <LuxPlusPriceStack size={priceStackSize} price={promotionalPrice.total} saleUnit={promotionalPrice.type} />
            )}
          </div>
        )}
        {!promotionalPrice && (
          <>
            <div>
              <PriceRowPriceCaption>
                <CruisePriceFromLabel />
              </PriceRowPriceCaption>
              <Group direction="horizontal" gap={4} verticalAlign="end">
                <LuxPlusPriceStack
                  size={priceStackSize}
                  price={primaryPrice.total}
                  memberPrice={memberLowestPrice?.primaryPrice.total}
                  saleUnit={primaryPrice.type}
                  mode={memberPriceType === 'STANDARD_PRICE' ? 'member-only' : undefined}
                />
              </Group>
            </div>
            {secondaryPrice && (
              <PriceRowCaption>
                <FormattedMessage
                  id="GS-URylh1YojT846ev_Q"
                  defaultMessage="{price}/{priceType} · Twin Share"
                  description="Secondary price display with twin share indicator in the cruises section"
                  values={{
                    price: <FormatCurrency value={secondaryPrice.total} />,
                    priceType: secondaryPrice.type,
                  }}
                />
              </PriceRowCaption>
            )}
          </>
        )}
      </Group>
      {!isCompact && <Group direction="vertical" gap={4}>
        {promotionalPrice && !!discountPills && discountPills.discountPrice > 0 && (
          <CruisePriceSaveUpCaption
            size="L"
            discountAmount={discountPills.discountPrice}
            discountPercentage={discountPills.discountPercentage}
            onInfoIconClick={openDiscountTsAndCsModal}
          />
        )}
        {!promotionalPrice && discountPills && (
          <PriceRowValueDiscountWithCaption
            size="L"
            originalValue={discountPills.price}
            discountPercentage={discountPills.discountPercentage}
            onInfoIconClick={openDiscountTsAndCsModal}
          />
        )}

        {canSeeLuxPlusLabel && <CruiseLuxPlusLabel isModalEnabled offer={offer} />}
      </Group>}
    </Group>
  )
}

export default CruiseOverviewPriceDetails
